import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../shared/ui/button/Button";
import Input from "../../../../shared/ui/form/input/InputField";
import Label from "../../../../shared/ui/form/Label";
import { Modal } from "../../../../shared/ui/modal";
import { GroupMemberPicker } from "../../../patient";
import { useCenterPatients, useCourseLabels, useEditCenterGroup } from "../../../center";
import type { CenterGroup } from "../../../center";
import { centerGroupLabel } from "../centerGroupLabel";
import { STUDENT_ERRORS } from "../refusals";

interface Props {
    centerId: string;
    /** The group being edited; the dialog is closed when null. */
    group: CenterGroup | null;
    mentorName: string;
    onClose: () => void;
}

/** The most students of one mentor offered at once. */
const CANDIDATES_LIMIT = 500;

/**
 * Editing one of a training centre's mentor groups: its name and who is in it.
 *
 * Members are the mentor's own students, as when the group was made; each
 * change is saved as it is made, as on a therapist's own group. Another
 * mentor is a handover, done from the list with "Reasignar".
 */
export default function CenterGroupEditModal({ centerId, group, mentorName, onClose }: Props) {
    const { t } = useTranslation();
    const { rename, add, remove } = useEditCenterGroup(centerId);
    const [name, setName] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        setName(group?.display_name ?? "");
        setError(null);
        setSaved(false);
    }, [group?.group_id, group?.display_name]);

    // Once the group has met, who is in it is its history (see the backend's
    // `UnitHasSessionsError`): members are shown, not changed.
    const hasMet = (group?.session_count ?? 0) > 0;
    // A training centre's student is named with the course they are on.
    const { labelOf } = useCourseLabels();
    const { patients, loading } = useCenterPatients(group && !hasMet ? centerId : undefined, {
        therapist_id: group?.therapist_id,
        limit: CANDIDATES_LIMIT,
    });

    if (!group) return null;

    const memberIds = new Set(group.members.map((member) => member.patient_id));
    const candidates = patients
        .filter((patient) => !patient.is_anonymous && !memberIds.has(patient.patient_id))
        .map((patient) => ({
            value: patient.patient_id,
            label: `${labelOf(patient)}${patient.contact_email ? ` · ${patient.contact_email}` : ""}`,
        }));

    const refuse = (err: unknown) => {
        const code = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        setError(t(STUDENT_ERRORS[code ?? ""] ?? "center.groups.errorGeneric"));
    };

    const run = async (action: () => Promise<unknown>) => {
        setError(null);
        setSaved(false);
        try {
            await action();
            return true;
        } catch (err) {
            refuse(err);
            return false;
        }
    };

    const saveName = async () => {
        if (await run(() => rename.mutateAsync({ groupId: group.group_id, displayName: name.trim() || null }))) {
            setSaved(true);
        }
    };

    return (
        <Modal
            isOpen
            onClose={onClose}
            ariaLabel={t("center.groups.editTitle")}
            className="mx-4 max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-surface-raised p-6"
        >
            <div className="flex flex-col gap-5">
                <div className="pr-14">
                    <p className="text-xs font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-300">
                        {t("center.groups.editTitle")}
                    </p>
                    <h3 className="mt-1 text-lg font-semibold text-content-primary">
                        {centerGroupLabel(t, group.display_name, group.members.length)}
                    </h3>
                    <p className="mt-1 text-sm text-content-tertiary">
                        {t("center.groups.mentorIs", { name: mentorName })}
                    </p>
                </div>

                <form
                    className="flex flex-col gap-2 sm:flex-row sm:items-end"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void saveName();
                    }}
                >
                    <div className="min-w-0 flex-1">
                        <Label htmlFor="group-edit-name">{t("center.students.groupNameOptional")}</Label>
                        <Input
                            id="group-edit-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t("center.students.groupNamePlaceholder")}
                        />
                    </div>
                    <Button
                        type="submit"
                        variant="outline"
                        loading={rename.isPending}
                        disabled={rename.isPending || name.trim() === (group.display_name ?? "")}
                    >
                        {t("common.save")}
                    </Button>
                </form>

                {hasMet ? (
                    <div>
                        <Label>{t("center.students.members")}</Label>
                        <ul className="mt-1 list-disc pl-5 text-sm text-content-primary">
                            {group.members.map((member) => (
                                <li key={member.patient_id}>{`${member.first_name} ${member.last_name}`}</li>
                            ))}
                        </ul>
                    </div>
                ) : (
                    <GroupMemberPicker
                        members={group.members.map((member) => ({
                            id: member.patient_id,
                            name: `${member.first_name} ${member.last_name}`,
                        }))}
                        candidates={candidates}
                        loading={loading}
                        disabled={add.isPending || remove.isPending}
                        onAdd={(patientId) => void run(() => add.mutateAsync({ groupId: group.group_id, patientId }))}
                        onRemove={(member) =>
                            void run(() => remove.mutateAsync({ groupId: group.group_id, patientId: member.id }))
                        }
                    />
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t(hasMet ? "center.groups.lockedHint" : "center.groups.mentorHint")}
                </p>

                {error && (
                    <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                        {error}
                    </p>
                )}
                {saved && (
                    <p role="status" className="text-sm text-brand-700 dark:text-brand-300">
                        {t("center.groups.saved")}
                    </p>
                )}

                <div className="flex justify-end">
                    <Button variant="outline" onClick={onClose}>
                        {t("common.close")}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
